/**
 * The Studio's audio player. One implementation, used by the composer's result
 * card and by the Studio drawer.
 *
 * The prototype had two players that looked similar and were built twice: the
 * composer's had a play button, a waveform and a scrubber, and the drawer's added
 * repeat, a volume slider, a speed select and a download icon in a permanent row
 * of five. Everything that was on screen is still reachable, but the transport is
 * now one line — play, waveform, time — and the rest is passed in as `secondary`,
 * which the drawer puts behind its More menu.
 *
 * The waveform sits on the same baseline as the play button and the clock, so it
 * reads as part of the transport rather than as a debug readout parked above it.
 *
 * SIMULATED. There is no <audio> element anywhere in the app: `elapsed` comes from an
 * interval in useSimulatedPlayback, whose period is set by the playback rate, and the
 * bars are a seeded PRNG, not amplitude. Props stay the same when real audio lands —
 * only the data source changes. See src/components/player/.
 */
import type { ReactNode } from 'react';
import { Loader2, Pause, Play } from 'lucide-react';
import { PlayerBar, Waveform } from '../player';
import { seededHeights } from '../../lib/waveform';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface TrackPlayerProps {
  song: Song;
  playing: boolean;
  /** Seconds buffered from the start of the media, when known. */
  bufferedSec?: number;
  /**
   * True when this song is loaded, running or paused. Defaults to `playing`.
   *
   * The scrubber reads it, the Play/Pause label does not — so pausing a result card
   * holds its position instead of resetting the bar to zero.
   */
  active?: boolean;
  elapsed: number;
  /** Playback rate, for the scrubber's smoothing. Defaults to 1×. See lib/playback. */
  speed?: number;
  /** True while a real audio-backed track is loading/buffering. */
  loading?: boolean;
  onTogglePlay: () => void;
  onSeek?: (seconds: number) => void;
  /** Number of waveform bars. The drawer is narrower than the composer. */
  bars?: number;
  /** Diameter of the play button. */
  size?: number;
  /** Right-aligned controls on the transport line, e.g. a More menu. */
  trailing?: ReactNode;
  /** Extra controls under the scrubber. The drawer's volume/speed row. */
  secondary?: ReactNode;
}

export default function TrackPlayer({
  song,
  playing,
  bufferedSec = 0,
  active = playing,
  elapsed,
  speed = 1,
  loading = false,
  onTogglePlay,
  onSeek,
  bars = 24,
  size = 38,
  trailing,
  secondary,
}: TrackPlayerProps) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={onTogglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: c.accentFill,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {loading ? (
            <Loader2 size={15} color="#1A1300" className="lyx-spin" />
          ) : playing ? (
            <Pause size={15} color="#1A1300" />
          ) : (
            // The 1px nudge optically centres a triangle in a circle.
            <Play size={15} color="#1A1300" style={{ marginLeft: 1 }} />
          )}
        </button>

        {/* The waveform takes the slack and spreads across it, so the transport is
            one continuous line from the play button to whatever sits on the right. */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Waveform heights={seededHeights(song.id, bars)} playing={playing} fill />
        </div>

        {trailing}
      </div>

      <div style={{ marginTop: 12 }}>
        <PlayerBar
          elapsedSec={active ? elapsed : 0}
          bufferedSec={bufferedSec}
          lengthStr={song.length}
          onSeek={onSeek}
          playing={playing}
          speed={speed}
        />
      </div>

      {secondary}
    </div>
  );
}
